import { create } from 'zustand';
import { produce } from 'immer';
import { getKeys } from 'shared/lib/object';
import { isObject } from 'shared/lib/type-guards';

import type { CustomHoliday, FormattedHolidays, Holiday } from '../model';
import { numberToMonthName, numberToWeekday } from '../constants';

interface HolidaysStoreState {
  initialHolidays: FormattedHolidays | null;
  holidays: FormattedHolidays | null;
  isWarning: boolean;
  isDirty: boolean;
}

interface HolidayActions {
  setIsDirty: () => void;
  setIsWarning: (isWarning: boolean) => void;
  setFullState: (formattedHolidays: FormattedHolidays | null) => void;
  setInitialState: () => void;
  reverseIsChecked: (holiday: Holiday | CustomHoliday) => void;
  removeHoliday: (holiday: Holiday | CustomHoliday) => void;
  addHoliday: (date: Date) => void;
  setMonthIsChecked: ({ year, month, isChecked }: { year: string; month: string; isChecked: boolean }) => void;
  setYearIsChecked: ({ year, isChecked }: { year: string; isChecked: boolean }) => void;
}

const defaultValues: HolidaysStoreState = {
  initialHolidays: null,
  holidays: null,
  isWarning: false,
  isDirty: false,
};

const useHolidayStore = create<HolidaysStoreState & { actions: HolidayActions }>((set, get) => ({
  ...defaultValues,
  actions: {
    setIsDirty: () => {
      const initialHolidays = JSON.stringify(get().initialHolidays);
      const holidays = JSON.stringify(get().holidays);

      set({ isDirty: initialHolidays !== holidays });
    },
    setFullState: (holidays) => {
      set({ holidays, initialHolidays: holidays });
      get().actions.setIsDirty();
    },
    setInitialState: () => {
      set({ ...defaultValues });
    },
    setIsWarning: (isWarning) => set({ isWarning }),
    reverseIsChecked: (holiday) => {
      set(
        produce((state: HolidaysStoreState) => {
          const { year, month, day } = holiday;

          if (state.holidays) {
            state.holidays[year][month][day].isChecked = !state.holidays[year][month][day].isChecked;
          }
        })
      );
      get().actions.setIsDirty();
    },
    addHoliday: (date) => {
      set(
        produce((state: HolidaysStoreState) => {
          const day = date.getDate();
          const month = numberToMonthName[date.getMonth() + 1];
          const year = date.getFullYear();
          const weekDay = numberToWeekday[date.getDay()];

          if (state.holidays && isObject(state.holidays[year][month][day])) {
            state.holidays[year][month][day].isChecked = true;

            return;
          }

          if (state.holidays && !isObject(state.holidays[year][month][day])) {
            state.holidays[year][month][day] = {
              isCustom: true,
              year,
              day,
              month,
              weekDay,
              isChecked: true,
              isAvailable: true,
              id: null,
              description: null,
            };
          }
        })
      );
      get().actions.setIsDirty();
    },
    removeHoliday: (holiday) => {
      set(
        produce((state: HolidaysStoreState) => {
          const { year, month, day } = holiday;

          if (state.holidays?.[year][month][day].isAvailable) {
            delete state.holidays[year][month][day];
          }
        })
      );
      get().actions.setIsDirty();
    },
    setMonthIsChecked: ({ year, month, isChecked }) => {
      const state = get();

      if (state.holidays) {
        getKeys(state.holidays[year][month]).forEach((day) => {
          if (!state.holidays?.[year][month][day].isAvailable) {
            get().actions.setIsWarning(true);
          }
        });
      }

      set(
        produce((state: HolidaysStoreState) => {
          if (state.holidays) {
            getKeys(state.holidays[year][month]).forEach((day) => {
              if (!state.holidays?.[year][month][day].isAvailable) {
                return;
              }

              state.holidays[year][month][day].isChecked = isChecked;
            });
          }
        })
      );

      get().actions.setIsDirty();
    },
    setYearIsChecked: ({ year, isChecked }) => {
      const state = get();

      if (state.holidays) {
        getKeys(state.holidays[year]).forEach((month) => {
          get().actions.setMonthIsChecked({ year, month, isChecked });
        });
      }
      get().actions.setIsDirty();
    },
  },
}));

export const useHolidaysValues = () => useHolidayStore((state) => state.holidays);
export const useHolidaysIsWarningValue = () => useHolidayStore((state) => state.isWarning);
export const useHolidaysIsDirtyValue = () => useHolidayStore((state) => state.isDirty);

export const useHolidaysActions = () => useHolidayStore((state) => state.actions);
